import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { Comment } from '@/types'
import * as commentApi from '../../services/api/comment'

interface CommentState {
  comments: Record<string, Comment[]> // issueId -> comments
  loading: boolean
  error: string | null
}

const initialState: CommentState = {
  comments: {},
  loading: false,
  error: null,
}

// 取得議題留言
export const fetchComments = createAsyncThunk(
  'comment/fetchComments',
  async (issueId: string) => {
    const response = await commentApi.getComments(issueId)
    return { issueId, comments: response.data?.comments || [] }
  }
)

// 新增留言
export const createComment = createAsyncThunk(
  'comment/createComment',
  async (data: { content: string; issueId: string }) => {
    const response = await commentApi.createComment(data)
    return { issueId: data.issueId, comment: response.data?.comment }
  }
)

// 更新留言
export const updateComment = createAsyncThunk(
  'comment/updateComment',
  async ({ commentId, content, issueId }: { commentId: string; content: string; issueId: string }) => {
    const response = await commentApi.updateComment(commentId, { content })
    return { issueId, comment: response.data?.comment }
  }
)

// 刪除留言
export const deleteComment = createAsyncThunk(
  'comment/deleteComment',
  async ({ commentId, issueId }: { commentId: string; issueId: string }) => {
    await commentApi.deleteComment(commentId)
    return { issueId, commentId }
  }
)

const commentSlice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearComments: (state, action) => {
      if (action.payload) {
        delete state.comments[action.payload]
      } else {
        state.comments = {}
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch comments
      .addCase(fetchComments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.loading = false
        const { issueId, comments } = action.payload
        state.comments[issueId] = comments
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '載入留言失敗'
      })
      // Create comment
      .addCase(createComment.fulfilled, (state, action) => {
        const { issueId, comment } = action.payload
        if (comment && state.comments[issueId]) {
          state.comments[issueId].unshift(comment) // 新留言添加到陣列開頭
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        state.error = action.error.message || '新增留言失敗'
      })
      // Update comment
      .addCase(updateComment.fulfilled, (state, action) => {
        const { issueId, comment } = action.payload
        if (comment && state.comments[issueId]) {
          const index = state.comments[issueId].findIndex(c => c.id === comment.id)
          if (index !== -1) {
            state.comments[issueId][index] = comment
          }
        }
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.error = action.error.message || '更新留言失敗'
      })
      // Delete comment
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { issueId, commentId } = action.payload
        if (state.comments[issueId]) {
          state.comments[issueId] = state.comments[issueId].filter(c => c.id !== commentId)
        }
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.error = action.error.message || '刪除留言失敗'
      })
  },
})

export const { clearError, clearComments } = commentSlice.actions
export default commentSlice.reducer